import { action, computed, runInAction } from 'mobx';
import { FormattedMessage } from 'react-intl';
import Store from './Store';
import { apiCall, apiFetch } from '@app/decorators/api';
import {
    fetchPrices,
    fetchSubscription,
    reactivateSubscription,
    resumeSubscription,
    switchSubscriptionPlan,
} from '@/api';
import SubscriptionModel from '../models/SubscriptionModel';
import { priceValueToInt, sendAmplitudeLogEvent } from '@/helpers';

class SubscriptionStore extends Store {
    static observables = {
        subscription: null,
        pricesByCurrency: null,
        isPaymentValidationErrorModalOpened: false,
    };

    /* ---- PRICING ---- */

    /**
     * Returns prices grouped by product for a given currency
     *
     * @example
     * getPricesOfCurrency('gbp');
     * {
     *     supersonic: {
     *         yearly: { base: "684 £GB", extra: "240 £GB" },
     *         monthly: { base: "69 £GB", extra: "20 £GB" },
     *     },
     *     boost: { ... },
     *     launch: { ... },
     *     import_rss_auto: { ... },
     *     upload_hours: { ... },
     *     newsletter: { ... },
     *     pro: { ... },
     *     studio: { ... },
     *     podcaster: { ... },
     * }
     */
    getPricesOfCurrency = (currency) => {
        if (!this.pricesByCurrency) {
            return null;
        }
        return this.pricesByCurrency[currency] ?? null;
    };

    /**
     * Returns prices grouped by periodicity for a given currency and product
     *
     * @example
     * getPricesOfCurrencyAndProduct('gbp', 'supersonic');
     * {
     *     yearly: {
     *         base: "684 £GB",
     *         extra: "240 £GB",
     *     },
     *     monthly: { ... },
     * }
     */
    getPricesOfCurrencyAndProduct = (currency, product) => {
        const pricesOfCurrency = this.getPricesOfCurrency(currency);
        if (!pricesOfCurrency) {
            return null;
        }
        return pricesOfCurrency[product] ?? null;
    };

    /**
     * Returns prices grouped by product for current subscription currency
     *
     * @see getPricesOfCurrency
     */
    @computed
    get pricesOfCurrentSubscriptionCurrency() {
        const currentSubscription = this.subscription;
        if (!currentSubscription) {
            return null;
        }
        return this.getPricesOfCurrency(currentSubscription.currency);
    }

    /**
     * Returns prices grouped by periodicity for current subscription currency and pricing
     *
     * @see getPricesOfCurrency
     */
    @computed
    get pricesOfCurrentSubscriptionPricing() {
        const currentSubscription = this.subscription;
        if (!currentSubscription) {
            return null;
        }
        return this.getPricesOfCurrencyAndProduct(
            currentSubscription.currency,
            currentSubscription.pricing,
        );
    }

    /**
     * Returns prices for current subscription currency, pricing and periodicity
     *
     * @example
     * this.pricesOfCurrentSubscription;
     * { base: "684 £GB", extra: "240 £GB" }
     */
    @computed
    get pricesOfCurrentSubscription() {
        const currentSubscription = this.subscription;
        if (!currentSubscription) {
            return null;
        }
        if (!this.pricesOfCurrentSubscriptionPricing) {
            return null;
        }
        return currentSubscription.yearly
            ? this.pricesOfCurrentSubscriptionPricing.yearly
            : this.pricesOfCurrentSubscriptionPricing.monthly;
    }

    /**
     * Fetch and store prices for all currencies, pricing/options and periodicity
     */
    @apiFetch
    async fetchPrices() {
        const { axios, ...pricesByCurrency } = await fetchPrices();
        runInAction(() => {
            this.pricesByCurrency = pricesByCurrency;
        });
    }

    /* -------- */

    @computed
    get showHeader() {
        return (
            this.subscription &&
            (this.subscription.onGrace || this.subscription.pastDue || this.subscription.ended)
        );
    }

    @action.bound
    showPaymentValidationErrorModal(bool) {
        this.isPaymentValidationErrorModalOpened = bool;
    }

    // API

    @apiFetch
    async fetchSubscription() {
        const { data } = await fetchSubscription();
        runInAction(() => {
            this.subscription = new SubscriptionModel(this, data);
        });
    }

    @apiCall
    async switchSubscriptionPlan(subscription, yearly, price, stripe) {
        const subscriptionPromise = switchSubscriptionPlan({ yearly, price });

        try {
            const { data } = await subscriptionPromise;
            subscription.updateData(data);
        } catch (error) {
            // If 3D Secure 2 is required, response status should be 402 Payment Required
            if (error.response?.status !== 402) {
                throw error;
            }

            if (
                error?.response?.data?.client_secret === undefined &&
                error?.response?.data?.message !== undefined
            ) {
                throw error;
            }

            const { error: errorPaymentValidation } = await this.confirmStripePayment(
                error?.response?.data?.client_secret,
                stripe,
            );
            sendAmplitudeLogEvent('Payment Page Viewed');
            if (errorPaymentValidation) {
                this.showPaymentValidationErrorModal(true);
                throw new Error();
            } else {
                const { data } = await fetchSubscription();
                runInAction(() => {
                    subscription.updateData(data);
                });
            }
        }
    }

    @apiCall
    async resume(subscription) {
        const { data } = await resumeSubscription();
        subscription.updateData(data);
    }

    @apiCall
    async reactivate(subscription, stripe) {
        try {
            const { data } = await reactivateSubscription();
            subscription.updateData(data);
        } catch (error) {
            // If 3D Secure 2 is required, response status should be 402 Payment Required
            if (error.response?.status !== 402) {
                throw error;
            }

            if (
                error?.response?.data?.client_secret === undefined &&
                error?.response?.data?.message !== undefined
            ) {
                throw new Error(error.response.data.message);
            }

            const { error: errorPaymentValidation } = await this.confirmStripePayment(
                error?.response?.data?.client_secret,
                stripe,
            );

            if (errorPaymentValidation) {
                this.showPaymentValidationErrorModal(true);
            } else {
                const { data } = await fetchSubscription();
                subscription.updateData(data);
            }
        }
    }

    async handlePaymentValidation(subscription, stripe) {
        if (subscription?.intentSecret) {
            const { error } = await this.confirmStripePayment(subscription.intentSecret, stripe);

            if (error) {
                this.showPaymentValidationErrorModal(true);
            }

            const { data } = await fetchSubscription();
            runInAction(() => {
                subscription.updateData(data);
            });
        }
    }

    @apiCall
    confirmStripePayment(intentSecret, stripe) {
        // confirmCardPayment function will open a popup to let user authenticate with 3D Secure 2
        // See https://stripe.com/docs/stripe-js/reference#stripe-confirm-card-payment
        return stripe.confirmCardPayment(intentSecret);
    }

    @action.bound
    addSubscriptionDataToGoogleTagManager(subscription) {
        const priceBaseAmount = priceValueToInt(subscription.priceBase);
        const priceExtraAmount = subscription.priceExtra
            ? priceValueToInt(subscription.priceExtra)
            : 0;
        const priceDiscountAmount = subscription.discount
            ? priceValueToInt(subscription.discount)
            : 0;
        const priceTotalAmount = priceBaseAmount + priceExtraAmount - priceDiscountAmount;

        // eslint-disable-next-line no-undef
        if (dataLayer) {
            // eslint-disable-next-line no-undef
            dataLayer.push({
                ecommerce: {
                    purchase: {
                        actionField: {
                            id: subscription.id,
                            revenue: `${priceTotalAmount}`,
                        },
                        products: [
                            {
                                name: subscription.subscriptionPricing,
                                price: this.pricesOfCurrentSubscription
                                    ? `${priceValueToInt(this.pricesOfCurrentSubscription.base)}`
                                    : '0',
                                quantity: 1,
                            },
                        ],
                    },
                },
                event: 'validatedTransaction',
            });
        }
    }

    @computed
    get getSubscription() {
        const paymentMethod = this?.state.subscriptionStore.subscription.yearly
            ? 'yearly'
            : 'monthly';
        return {
            offer: this?.state.subscriptionStore.subscription.subscriptionPricing,
            currentSubscription: paymentMethod,
            currentMethodPayment:
                paymentMethod === 'yearly' ? (
                    <FormattedMessage defaultMessage="an" />
                ) : (
                    <FormattedMessage defaultMessage="mois" />
                ),
        };
    }
}

export default SubscriptionStore;
